import React, { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useSetState } from 'ahooks';
import { Modal, Button, Form } from 'tea-component';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import aegisLog from '@/log/aegis';
import { isSafari, getDownloadFileName } from '../../utils';
import { DownLoadConfig } from '../../instance/config';
import { getDownloadTaskId } from '../../../../services/download';
import { BusinessFormType, DownloadSettingModalProps } from '../../types';
import TipContext from '@/pages/home/components/TipContextComponents/TipContext';
import { SELECT_TOP, DOWNLOAD_TITLE_TIPS } from '../../instance/const';
import { BOOLEAN_2_NUM_FIELDS } from './const';
import { getBasePath } from '@/utils/utils';
import {
  getDownloadMetricsByType,
  sortDownloadMetricsByOptionsOrder,
  updateCascaderMetricOptionsDisabledStateAndDefaultValue,
  convertBoolean2Num,
  getDownloadSet,
} from '@intelligence/components/ExportTable/components/DownloadFormModal/utils';
import { downloadAction } from '@intelligence/components/DownloadCommon/DownloadPage';
import FormContent from '@intelligence/components/Form';
import useFormContent from '@intelligence/components/Form/useFormContent';
import { convertUniversalDownloadPopupParams } from '../middleware/common';
import { IChildProp } from '../../../Form/type';
import '@intelligence/components/ExportTable/components/DownloadSettingModal/index.scss';
const whiteListDownloadKey = [
  'mobileStoreRankMore',
  'consoleStoreRankMore',
  'mobileStoreRankTop',
  'consoleStoreRankTop',
];

// 新下载Form配置组件
const DownloadFormModal: FC<DownloadSettingModalProps> = (props) => {
  const { formatMessage } = useIntlExtend();
  const {
    total = 0,
    visible,
    loading,
    entityType,
    businessType,
    setLoading,
    setModalVisible,
    downloadModalSize,
    downloadLogConfigKey,
    classModalName,
    downloadTitle = formatMessage({ id: 'dbi_download_settings' }),
    isDisableDataAmount = false,
    tableParams = {},
    isShowMaxDownloadRowsTips,
    downloadTitleTips = DOWNLOAD_TITLE_TIPS,
    isOnlyExportAllData = false,
    isNeedCheckedLines = false,
    defaultCheckedLines = false,
    isSyncPageSizeWithSelectTop = false,
    getCheckedLinesCfg,
    dataType,
    excelName,
    pageId,
    newStyleFileName,
    gsdDevicesFlatten,
    downloadLogConfig,
    useWebDownload = false,
    downloadKey = '',
    stickText,
    hideStickText,
    regionOptions,
  } = props;

  // console.log('DownloadFormModal-props', props);

  const cascaderDeviceValueList = useRef<any>(null);

  const DefaultDataMethod = useMemo(() => {
    switch (businessType) {
      case 'combine-PCConsoleMetricsTable':
      case 'mobile-activeuser':
      case 'mobile-activeuser-studio':
      case 'mobile-revenue':
      case 'mobile-downloads':
      case 'mobile-rpd':
      case 'mobile-demographics':
      case 'mobile-upcoming':
        return 'selectTop';
      default:
        return defaultCheckedLines && isNeedCheckedLines
          ? 'checkedLines'
          : 'selectAll';
    }
  }, [businessType]);

  const initialSelectTop = useMemo(() => {
    switch (businessType) {
      case 'combine-PCConsoleMetricsTable':
      case 'mobile-activeuser':
      case 'mobile-revenue':
      case 'mobile-downloads':
      case 'mobile-rpd':
      case 'mobile-demographics':
      case 'mobile-upcoming':
      case 'mobile-retention':
        return 100;
      default:
        return total > SELECT_TOP ? SELECT_TOP : total;
    }
  }, [businessType]);

  const downloadSet = useMemo(() => getDownloadSet(businessType), [businessType],);

  const [formModel, setFormModelState] = useSetState<BusinessFormType>({
    data_method: DefaultDataMethod,
    data_amount: initialSelectTop,
    file_format: '0',
  });

  const openCommonDownloadPage = async (params: any) => {
    try {
      setLoading(true);
      const api = DownLoadConfig?.[businessType]?.apiUrl || '';
      if (!api) return;
      if (isSafari()) {
        const page: any = window.open('', '_blank');
        const taskId = await getDownloadTaskId(api, params);
        page.location = `${
          location.origin
        }${getBasePath()}/intelligence/download?entityType=${entityType}&taskId=${taskId}&isShowMaxDownloadRowsTips${isShowMaxDownloadRowsTips}`;
      } else {
        const taskId = await getDownloadTaskId(api, params);
        window.open(
          `${getBasePath()}/intelligence/download?entityType=${entityType}&taskId=${taskId}&isShowMaxDownloadRowsTips=${isShowMaxDownloadRowsTips}`,
          '_blank',
        );
      }
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
      setModalVisible(false);
    }
  };

  const handleDownloadLog = (logConfigKey?: string) => {
    // @ts-ignore
    const logConfig = dbiBtnLogMes[logConfigKey];
    downloadLogConfig && buriedBtnPoint(downloadLogConfig);
    if (!logConfig) return;
    buriedBtnPoint(logConfig);
  };

  const handleDownload = () => {
    formModel.data_amount =
      formModel?.data_amount > Number(total)
        ? Number(total)
        : formModel?.data_amount;
    const dataAmount = !isDisableDataAmount
      ? {
          select_top:
            formModel?.data_method === 'selectTop'
              ? formModel?.data_amount
              : Number(total),
        }
      : {};
    if (isOnlyExportAllData) {
      dataAmount.select_top = Number(total);
    }
    if (
      whiteListDownloadKey.includes(downloadKey) &&
      formModel?.data_method == 'selectAll'
    ) {
      dataAmount.select_top = 0;
    }
    let params: any = {
      ...tableParams,
      ...formModel,
      ...dataAmount,
      file_format: Number(formModel?.file_format),
      file_name: excelName || '',
      // 新风格全局排查文件名规范
      new_style_donwload_file_name: getDownloadFileName(
        businessType,
        newStyleFileName || '',
        entityType,
      ),
    };
    convertBoolean2Num(params, BOOLEAN_2_NUM_FIELDS);
    sortDownloadMetricsByOptionsOrder(params, formList);

    if (isSyncPageSizeWithSelectTop) {
      params.page_size = params.select_top;
    }
    const downloadModalParams = convertUniversalDownloadPopupParams(
      formValues,
      addFields.additionalFields,
      config,
    );
    params = {
      ...params,
      ...downloadModalParams,
    };
    // console.log('businessType', businessType);
    switch (businessType) {
      case 'marketProfile-stats': {
        params = {
          ...params,
          // 新风格全局排查文件名规范
          new_style_donwload_file_name: getDownloadFileName(
            'marketProfile-stats',
            newStyleFileName || '',
            entityType,
          ),
          // select_top: formModel.data_method === 'selectAll' ? 9999999 : formModel.data_amount,
        };
        break;
      }

      default:
        break;
    }
    delete params.data_amount;
    delete params.data_method;

    if (
      Object.prototype.hasOwnProperty.call(downloadModalParams, 'select_top')
    ) {
      delete params.select_ids;
    } else if (
      Object.prototype.hasOwnProperty.call(downloadModalParams, 'select_ids')
    ) {
      delete params.select_top;
    }

    if (
      isNeedCheckedLines &&
      typeof getCheckedLinesCfg === 'function' &&
      formModel?.data_method === 'checkedLines'
    ) {
      const { key, value } = getCheckedLinesCfg();
      params[key] = value;
    }

    aegisLog.reportEvent({
      name: 'dbi-download-configs-settings',
      msg: 'set download configs',
      ext1: JSON.stringify(formModel || {}),
      ext2: JSON.stringify(params || {}),
      // ext3: '',
    });

    if (useWebDownload) {
      handleDownloadLog(downloadLogConfigKey);
      downloadAction({
        downLoadParams: params,
        downloadKey,
      });
      setModalVisible(false);
      return;
    }

    handleDownloadLog(downloadLogConfigKey);
    openCommonDownloadPage(params);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };
  const isFormValid = useMemo(() => {
    if (!formModel) return false;
    const { metrics, data_method, data_amount } = formModel;
    // console.log('formModelr23', formModel);

    // 检测formModel中 是否存在metrics字段，存在则做必选校验（多数情况下载表单 有metrics，就会有metrics的必选校验）
    if (Reflect.has(formModel, 'metrics')) {
      if (!Array.isArray(metrics) || !metrics.length) return false;
    }
    if (data_method === 'selectTop' && data_amount < 1) return false;
    // if (isBlur) return false;

    if (formModel?.data_sources && formModel?.data_sources?.length < 1)
      return false;

    return true;
  }, [formModel, cascaderDeviceValueList?.current]);

  /** 可对特殊下载的formList进行修改 */
  const formList = useMemo(() => {
    const listTmp = DownLoadConfig?.[businessType]?.formList || [];
    const list = updateCascaderMetricOptionsDisabledStateAndDefaultValue(
      listTmp,
      {
        pageId,
        tableParams,
      },
    );
    switch (businessType) {
      default:
        return list;
    }
  }, [tableParams, businessType, gsdDevicesFlatten]);

  useEffect(() => {
    // 表单默认值处理 扁平化
    let defaultFormData =
      formList?.reduce((result, item) => {
        if (!!item?.children?.length) {
          item?.children?.forEach((config: any) => {
            if (config?.field) {
              if (item?.type === 'checkbox') {
                result = {
                  ...result,
                  ...config?.defaultValue,
                };
              } else {
                result[config?.field] = config?.defaultValue;
              }
            }
          });
        } else {
          if (item?.field) {
            if (item?.type === 'checkbox') {
              result = {
                ...result,
                ...item?.defaultValue,
              };
            } else {
              result[item?.field] = item?.defaultValue;
            }
          }
        }
        return result;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      }, {} as { [key: string]: any }) || {};

    // tableParams传递下来的默认值处理
    switch (businessType) {
      case 'marketProfile-stats':
        defaultFormData = {
          ...defaultFormData,
          entity_type: entityType,
          download_metrics: getDownloadMetricsByType(dataType),
        };
        break;
      default:
        break;
    }
    setFormModelState(defaultFormData);
  }, [businessType, tableParams, visible, gsdDevicesFlatten, formList]);

  /**
   * TODO：表单校验逻辑
   * 在不同的场景下添加附加字段，用于下载弹窗中的条件判断
   * @param type 下载弹窗类型
   * @returns
   */
  const getFormValueAdd = (type: string) => {
    const {} = tableParams;
    switch (type) {
      case 'mobileMarketProfileTopCharts':
        return {
          initialSelectTop,
          total,
          pageId,
          specialFields: {
            marketsSelected: tableParams?.tableAction?.selectedList?.map(
              (item: any) => item.market,
            ),
          },
        };
      case 'combine-detail-steamoverlap':
      case 'combine-detail-othervendor-overlap':
        return {
          regionCountrySelectMultiple: {
            value: {
              type: tableParams?.region_type,
              value: [tableParams?.region],
            },
          },
        };
      default:
        return {};
    }
  };

  const { config, addFields, formValues, setValues } = useFormContent(
    downloadSet,
    getFormValueAdd(businessType),
  );
  return (
    <Modal
      visible={visible}
      size={downloadModalSize}
      onClose={handleCloseModal}
      className={`dbi-download-type ${classModalName || ''}`}
      caption={
        <div className="download-head">
          <span>{downloadTitle}</span>
          {downloadTitleTips && (
            <TipContext
              className="tip-text-hd"
              tipText={formatMessage({ id: downloadTitleTips })}
            />
          )}
        </div>
      }
    >
      <Modal.Body>
        <Form
          className={`dbi-download-form teaui-form-alignment dbi-download-form-modal-comp ${
            businessType || ''
          }`}
        >
          <FormContent
            formList={config}
            formValues={formValues}
            addFields={addFields}
            onChange={setValues}
            regionOptions={regionOptions}
            tableParams={tableParams}
          />
          <Form.Item label="">
            {!hideStickText ? (
              <Form.Text style={{ color: '#9f9f9f' }}>
                {stickText ||
                  formatMessage({ id: 'dbi_download_modal_stick_tips' })}
              </Form.Text>
            ) : null}
          </Form.Item>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          loading={loading}
          disabled={!isFormValid}
          onClick={handleDownload}
        >
          {formatMessage({ id: 'dbi_confirm' })}
        </Button>
        <Button type="weak" onClick={handleCloseModal}>
          {formatMessage({ id: 'dbi_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DownloadFormModal;
